import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Demo practice ID for initial development
const DEMO_PRACTICE_ID = 'demo-practice-001';

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Get practice ID from context or use demo
export const getPracticeId = () => {
  // In production, this would come from auth/context
  return localStorage.getItem('practiceId') || DEMO_PRACTICE_ID;
};

// Treatments API
export const treatmentsApi = {
  getAll: async (practiceId = getPracticeId()) => {
    const response = await api.get(`/treatments?practiceId=${practiceId}`);
    return response.data;
  },
  
  create: async (treatmentData) => {
    const response = await api.post('/treatments', {
      ...treatmentData,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  update: async (treatmentId, updates) => {
    const response = await api.put(`/treatments/${treatmentId}`, updates);
    return response.data;
  },
  
  delete: async (treatmentId) => {
    const response = await api.delete(`/treatments/${treatmentId}`);
    return response.data;
  },
};

// Memberships API
export const membershipsApi = {
  getAll: async (practiceId = getPracticeId()) => {
    const response = await api.get(`/memberships?practiceId=${practiceId}`);
    return response.data;
  },
  
  create: async (membershipData) => {
    const response = await api.post('/memberships', {
      ...membershipData,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  update: async (membershipId, updates) => {
    const response = await api.put(`/memberships/${membershipId}`, updates);
    return response.data;
  },
  
  delete: async (membershipId) => {
    const response = await api.delete(`/memberships/${membershipId}`);
    return response.data;
  },
};

// Appointments API
export const appointmentsApi = {
  getAll: async (practiceId = getPracticeId(), filters = {}) => {
    let url = `/appointments?practiceId=${practiceId}`;
    if (filters.status) url += `&status=${filters.status}`;
    if (filters.date) url += `&date=${filters.date}`;
    const response = await api.get(url);
    return response.data;
  },
  
  create: async (appointmentData) => {
    const response = await api.post('/appointments', {
      ...appointmentData,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  update: async (appointmentId, status, notes) => {
    const response = await api.put(`/appointments/${appointmentId}?status=${status}${notes ? `&notes=${notes}` : ''}`);
    return response.data;
  },
  
  delete: async (appointmentId) => {
    const response = await api.delete(`/appointments/${appointmentId}`);
    return response.data;
  },
};

// Practices API
export const practicesApi = {
  signup: async (practiceData) => {
    const response = await api.post('/practices/signup', practiceData);
    return response.data;
  },
  
  getBranding: async (subdomain) => {
    const response = await api.get(`/practices/branding/${subdomain}`);
    return response.data;
  },
  
  updateBranding: async (practiceId, updates) => {
    const response = await api.put(`/practices/branding/${practiceId}`, updates);
    return response.data;
  },
  
  get: async (practiceId) => {
    const response = await api.get(`/practices/${practiceId}`);
    return response.data;
  },
};

// SMS API
export const smsApi = {
  send: async (toPhone, message) => {
    const response = await api.post('/sms/send', {
      to_phone: toPhone,
      message,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  sendBulk: async (recipients, message) => {
    const response = await api.post('/sms/send-bulk', {
      recipients,
      message,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  sendAppointmentReminder: async (reminderData) => {
    const response = await api.post('/sms/appointment-reminder', {
      ...reminderData,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  sendBookingConfirmation: async (confirmationData) => {
    const response = await api.post('/sms/booking-confirmation', {
      ...confirmationData,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  testConfig: async () => {
    const response = await api.get('/sms/test');
    return response.data;
  },
};

// Data Import API
export const dataImportApi = {
  import: async (importType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('practiceId', getPracticeId());
    
    const response = await api.post(`/import/${importType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Chatbot API
export const chatbotApi = {
  sendMessage: async (message, sessionId = null) => {
    const response = await api.post('/chatbot/message', {
      message,
      session_id: sessionId,
      practiceId: getPracticeId(),
    });
    return response.data;
  },
  
  getHistory: async (sessionId) => {
    const response = await api.get(`/chatbot/history?session_id=${sessionId}`);
    return response.data;
  },
  
  clearSession: async (sessionId) => {
    const response = await api.delete(`/chatbot/session/${sessionId}`);
    return response.data;
  },
  
  getStatus: async () => {
    const response = await api.get('/chatbot/status');
    return response.data;
  },
};

// Skin conditions for filtering (static data)
export const skinConditions = [
  { id: 'wrinkles', name: 'Wrinkles & Fine Lines' },
  { id: 'fine-lines', name: 'Fine Lines' },
  { id: 'forehead-lines', name: 'Forehead Lines' },
  { id: 'acne', name: 'Acne & Breakouts' },
  { id: 'pigmentation', name: 'Dark Spots & Pigmentation' },
  { id: 'dull-skin', name: 'Dull Skin' },
  { id: 'volume-loss', name: 'Volume Loss' },
  { id: 'thin-lips', name: 'Thin Lips' },
  { id: 'acne-scars', name: 'Acne Scars' },
  { id: 'dehydration', name: 'Dry & Dehydrated Skin' },
  { id: 'texture', name: 'Rough Texture' },
  { id: 'unwanted-hair', name: 'Unwanted Hair' },
];

// Providers (to be moved to backend later)
export const providers = [
  {
    id: 'p1',
    name: 'Dr. Emily Chen',
    specialty: 'Injectables & Facial Aesthetics',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200',
    availability: ['Monday', 'Tuesday', 'Wednesday', 'Friday'],
  },
  {
    id: 'p2',
    name: 'Dr. Michael Torres',
    specialty: 'Laser Treatments & Skin Rejuvenation',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200',
    availability: ['Tuesday', 'Thursday', 'Friday', 'Saturday'],
  },
];

export default api;
